import React, { useState, useEffect, useCallback, useContext } from 'react'
import { TrendingUp, BarChart2, Users, Globe, CheckCircle, RefreshCw, Clock, ChevronRight } from 'lucide-react'
import styles from './AppAnalyticsHome.module.css'
import analyticsClient, { defaultDateRange, setAnalyticsSecret } from '../../../api/analyticsClient'
import AuthContext from '../../../store/auth-context'
import GrowthSection from './sections/GrowthSection'
import EngagementSection from './sections/EngagementSection'
import RetentionSection from './sections/RetentionSection'
import LanguageSection from './sections/LanguageSection'
import SupplySection from './sections/SupplySection'

const TABS = [
  { id: 'growth', label: 'Growth', icon: TrendingUp, description: 'Registrations, channels, geography' },
  { id: 'engagement', label: 'Engagement', icon: BarChart2, description: 'DAU/WAU/MAU, API usage, errors' },
  { id: 'retention', label: 'Retention', icon: Users, description: 'Cohorts, churn, FCM reachability' },
  { id: 'language', label: 'Language', icon: Globe, description: 'Language distribution and trends' },
  { id: 'supply', label: 'Supply Quality', icon: CheckCircle, description: 'Profiles, salary, verification' },
]

const AppAnalyticsHome = () => {
  const authCtx = useContext(AuthContext)
  const [activeTab, setActiveTab] = useState('growth')
  const [dateRange, setDateRange] = useState(defaultDateRange())
  const [dataRange, setDataRange] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [metaLoading, setMetaLoading] = useState(true)

  const loadMeta = useCallback(async () => {
    setMetaLoading(true)
    const [rangeRes, updatedRes] = await Promise.all([
      analyticsClient.get('/analytics/meta/data-range').catch(() => null),
      analyticsClient.get('/analytics/meta/last-updated').catch(() => null),
    ])
    if (rangeRes?.data) setDataRange(rangeRes.data)
    if (updatedRes?.data) setLastUpdated(updatedRes.data)
    setMetaLoading(false)
  }, [])

  useEffect(() => {
    setAnalyticsSecret(authCtx.userIdentifier)
    loadMeta()
  }, [authCtx.userIdentifier, loadMeta])

  const minDate = dataRange?.logs?.logs_from?.slice(0, 10) || '2024-01-01'
  const maxDate = dataRange?.logs?.logs_to?.slice(0, 10) || new Date().toISOString().slice(0, 10)

  const formatLastUpdated = () => {
    if (!lastUpdated?.last_log_at) return null
    const d = new Date(lastUpdated.last_log_at)
    return d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    }) + ' IST'
  }

  const activeTabConfig = TABS.find(t => t.id === activeTab)

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbParent}>Analytics</span>
            <ChevronRight size={13} className={styles.breadcrumbSep} />
            <span className={styles.breadcrumbCurrent}>App</span>
          </div>
          <h1 className={styles.title}>App Analytics</h1>
          <p className={styles.titleSub}>{activeTabConfig?.description}</p>
        </div>

        <div className={styles.topbarRight}>
          <div className={styles.dateGroup}>
            <label className={styles.dateLabel}>From</label>
            <input
              type="date"
              className={styles.dateInput}
              value={dateRange.from}
              min={minDate}
              max={dateRange.to}
              onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))}
            />
            <span className={styles.dateSep}>—</span>
            <label className={styles.dateLabel}>To</label>
            <input
              type="date"
              className={styles.dateInput}
              value={dateRange.to}
              min={dateRange.from}
              max={maxDate}
              onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))}
            />
          </div>

          <button
            className={`${styles.refreshBtn} ${metaLoading ? styles.refreshBtnSpin : ''}`}
            onClick={loadMeta}
            title="Refresh meta"
            disabled={metaLoading}
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {lastUpdated?.last_log_at && (
        <div className={styles.freshnessBanner}>
          <Clock size={11} />
          <span>Data as of {formatLastUpdated()}</span>
          {lastUpdated?.total_log_entries && (
            <span className={styles.freshnessCount}>
              {Number(lastUpdated.total_log_entries).toLocaleString('en-IN')} total log entries
            </span>
          )}
        </div>
      )}

      <div className={styles.tabBar}>
        {TABS.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={14} className={styles.tabIcon} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      <div className={styles.content}>
        {activeTab === 'growth' && <GrowthSection dateRange={dateRange} />}
        {activeTab === 'engagement' && <EngagementSection dateRange={dateRange} />}
        {activeTab === 'retention' && <RetentionSection dateRange={dateRange} />}
        {activeTab === 'language' && <LanguageSection dateRange={dateRange} />}
        {activeTab === 'supply' && <SupplySection />}
      </div>
    </div>
  )
}

export default AppAnalyticsHome
